import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import { formatText } from "../i18n/texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { closeDialog, showDialog } from "../ui/dialog";
import { FieldError, SectionMessage } from "../ui/feedback";
import { ChatIcon } from "../ui/icons/Icon";
import { TaskWindow } from "../ui/TaskWindow";
import { ContactRequestFailure } from "./contactDirectoryClient";
import {
  hasImportPages,
  importCountKey,
  phone,
  sourceSubtitle,
  writableFields,
  type ContactImportFileResult,
  type ContactImportItem,
  type ContactImportResult,
  type ContactImportTagChoice,
  type ContactImportTagResolution,
  type TagDefinition,
} from "./contactModel";
import { ContactTagBadge } from "./ContactTagBadge";
import styles from "./ContactImportWizard.module.css";
import { viewState } from "./contactViewState";
import type { ContactWorkspaceController, ImportWizardHandle } from "./contactWorkspaceController";
import { TagCatalogClient } from "./tagCatalogClient";
import { downloadText, focusField } from "./workspaceDom";

// Importar contactos (ContactImportWizard.razor, ContactImportMapping.razor y ContactImportPreviewTable.razor del portal
// actual): cuatro pasos (Archivo, Columnas, Vista previa y Resultado) en una ventana de tarea. El archivo y las
// columnas viven en memoria de la pestaña (nunca en sessionStorage); el lote lo guarda y valida el servidor.

const phaseKeys = ["FILE", "MAPPING", "PREVIEW", "RESULT"];
const newKey = () => crypto.randomUUID().replace(/-/g, "");
const template =
  "Nombre,Apellidos,Correo,Teléfono,Relación,Etiquetas\r\nRosa,Palacios,rosa.palacios@example.test,+51900111210,Cliente,QA\r\n";

type Props = { ctl: ContactWorkspaceController };

/** «{n} columnas en tu archivo · {n} asignadas · {n} se omiten», sin cortar cada parte en dos líneas. */
export function mappingSummary(
  ctl: ContactWorkspaceController,
  file: ContactImportFileResult,
  mapping: Record<string, number>,
) {
  const assigned = Object.keys(mapping).length;
  const skipped = file.headers.length - new Set(Object.values(mapping)).size;
  const part = (text: string) => text.replace(/ /g, " ");
  return (
    part(formatText(ctl.L2(file.headers.length === 1 ? "FILE_COLUMNS_ONE" : "FILE_COLUMNS"), file.headers.length)) +
    " · " +
    part(formatText(ctl.L2(assigned === 1 ? "ASSIGNED_ONE" : "ASSIGNED"), assigned)) +
    " · " +
    part(formatText(ctl.L2(skipped === 1 ? "SKIPPED_ONE" : "SKIPPED"), skipped))
  );
}

export const ContactImportWizard = forwardRef<ImportWizardHandle, Props>(function ContactImportWizard({ ctl }, ref) {
  const s = styles.scope;
  const api = ctl.api;
  const taskWindow = useRef<HTMLDialogElement>(null);
  const phaseTitle = useRef<HTMLParagraphElement>(null);
  const resolverTitle = useRef<HTMLHeadingElement>(null);
  const [text, setText] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [separator, setSeparator] = useState("AUTO");
  const [key, setKey] = useState(newKey);
  const [detectedSeparator, setDetectedSeparator] = useState("\0");
  const [fileOptionsOpen, setFileOptionsOpen] = useState(false);
  const [file, setFile] = useState<ContactImportFileResult | null>(null);
  const [batch, setBatch] = useState<ContactImportResult | null>(null);
  const [rememberedTagChoices, setRememberedTagChoices] = useState<ContactImportTagChoice[]>([]);
  const [resolvingTag, setResolvingTag] = useState<ContactImportTagResolution | null>(null);
  const [tagOptions, setTagOptions] = useState<TagDefinition[]>([]);
  const [tagSearch, setTagSearch] = useState("");
  const [tagMore, setTagMore] = useState(false);
  const [mapping, setMapping] = useState<Record<string, number>>({});
  const [phase, setPhase] = useState(1);
  const [busy, setBusy] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [bheliExport, setBheliExport] = useState(false);
  const [validation, setValidation] = useState(false);
  const [separatorError, setSeparatorError] = useState(false);
  const [errorField, setErrorField] = useState("FirstName");
  const [focus, setFocusRequest] = useState<{ target: "phase" | "resolver" | "error"; count: number } | null>(null);
  const setFocus = (target: "phase" | "resolver" | "error") =>
    setFocusRequest((previous) => ({ target, count: (previous?.count ?? 0) + 1 }));
  // Lo que no se dibuja: autoridad de la preparación, la generación y la búsqueda de etiquetas.
  const work = useRef({
    lifetime: new AbortController(),
    owner: null as string | null,
    session: null as string | null,
    visible: false,
    busy: false,
    tagRevision: 0,
    tagPage: 1,
    tagApi: null as TagCatalogClient | null,
  });
  // Copia viva de lo que la ventana conserva entre navegaciones (PreservePreparation).
  const live = useRef({
    text,
    fileName,
    separator,
    key,
    detectedSeparator,
    fileOptionsOpen,
    file,
    mapping,
    phase,
    pendingOnly,
    bheliExport,
    batch,
    rememberedTagChoices,
  });
  useEffect(() => {
    live.current = {
      text,
      fileName,
      separator,
      key,
      detectedSeparator,
      fileOptionsOpen,
      file,
      mapping,
      phase,
      pendingOnly,
      bheliExport,
      batch,
      rememberedTagChoices,
    };
  });

  // El foco que pidió el último paso, una vez dibujado (cada pedido es nuevo aunque repita el destino).
  const where = useRef({ phase, separatorError });
  useEffect(() => {
    where.current = { phase, separatorError };
  });
  useEffect(() => {
    if (focus?.target === "phase") phaseTitle.current?.focus();
    else if (focus?.target === "resolver") resolverTitle.current?.focus();
    else if (focus?.target === "error")
      focusField(
        where.current.phase === 2
          ? "c1-mapping-list"
          : where.current.separatorError
            ? "c2-import-separator"
            : "c1-import-file",
      );
  }, [focus]);

  const hasPreparation = () => live.current.text !== null || live.current.batch !== null;
  const owner = () => `${ctl.tenant}:${ctl.user}`;
  const canPrepare = () => {
    const rights = api.currentRights;
    return !!rights && rights.canRead && rights.canWrite && rights.globalScope && rights.canImport;
  };
  const rememberOwner = () => {
    work.current.owner = owner();
    work.current.session = api.currentSessionStamp;
  };
  const requireAuthority = () => {
    if (
      work.current.owner !== null &&
      (work.current.owner !== owner() || work.current.session !== api.currentSessionStamp)
    )
      throw new ContactRequestFailure(401, "SESSION_INVALID");
    if (!canPrepare()) throw new ContactRequestFailure(403, "ACCESS_REVOKED");
  };
  const clearTagResolver = () => {
    setRememberedTagChoices([]);
    work.current.tagRevision++;
    setResolvingTag(null);
    work.current.tagApi = null;
    setTagOptions([]);
    setTagSearch("");
    setTagMore(false);
    work.current.tagPage = 1;
  };
  const invalidate = (message?: string) => {
    viewState().importPreparation = null;
    work.current.owner = null;
    work.current.session = null;
    work.current.visible = false;
    work.current.lifetime.abort();
    work.current.lifetime = new AbortController();
    clearTagResolver();
    setText(null);
    setFileName(null);
    setFile(null);
    setBatch(null);
    setMapping({});
    setKey(newKey());
    setDetectedSeparator("\0");
    setPhase(1);
    setBusy(false);
    work.current.busy = false;
    setError(message ?? ctl.L("TENANT_CHANGED"));
  };
  const preserve = () => {
    const value = live.current;
    if (value.text === null && value.batch === null) {
      viewState().importPreparation = null;
      return;
    }
    if (work.current.owner !== viewState().owner || work.current.session !== api.currentSessionStamp || !canPrepare())
      return;
    viewState().importPreparation = {
      owner: work.current.owner!,
      sessionStamp: work.current.session,
      text: value.text,
      fileName: value.fileName,
      separator: value.separator,
      key: value.key,
      detectedSeparator: value.detectedSeparator,
      fileOptionsOpen: value.fileOptionsOpen,
      file: value.file,
      mapping: { ...value.mapping },
      phase: value.phase,
      pendingOnly: value.pendingOnly,
      bheliExport: value.bheliExport,
      visible: work.current.visible,
      batchId: value.batch?.id ?? null,
      tagChoices: value.rememberedTagChoices,
    };
  };
  const hasTagReview = (value: ContactImportResult | null) =>
    value?.tagResolutions?.some(
      (item) => item.target === null && (item.kind === "REVIEW" || item.kind === "ARCHIVED"),
    ) === true;

  /** Corre un paso con la autoridad viva: el contexto otra vez, la misma persona y sesión, y el permiso de importar. */
  const run = async (action: (signal: AbortSignal) => Promise<void>) => {
    if (work.current.busy) return;
    const generation = work.current.lifetime;
    work.current.busy = true;
    setBusy(true);
    setError(null);
    setValidation(false);
    setSeparatorError(false);
    try {
      await api.ensureContext(generation.signal);
      requireAuthority();
      if (generation.signal.aborted) throw new DOMException("cancelado", "AbortError");
      await action(generation.signal);
    } catch (exception) {
      if (generation !== work.current.lifetime) return;
      let message = ctl.failure(exception);
      const code = exception instanceof ContactRequestFailure ? exception.code : null;
      const current = live.current;
      if (
        current.phase === 3 &&
        current.batch &&
        (code === "STALE_VERSION" || code === "TAG_ARCHIVED" || code === "TAG_DUPLICATE_ARCHIVED")
      ) {
        try {
          const loaded = await api.getImport(current.batch.id, generation.signal);
          setBatch(loaded);
          if (hasTagReview(loaded)) setPhase(3);
        } catch {
          /* Queda el error canónico; la guarda de autoridad retira el contenido. */
        }
      }
      let separatorProblem = false;
      if (code === "IMPORT_DELIMITER_AMBIGUOUS") {
        setFileOptionsOpen(true);
        separatorProblem = true;
        message = ctl.L2("DELIMITER_AMBIGUOUS");
      }
      if (current.phase === 1 && code === "IMPORT_FORMAT") {
        setFileOptionsOpen(true);
        separatorProblem = true;
        message = ctl.failure(exception) + " " + ctl.L2("IMPORT_SEPARATOR");
      }
      setSeparatorError(separatorProblem);
      setError(message);
      const invalid =
        (current.phase === 1 || current.phase === 2) &&
        ["IMPORT_MAPPING", "IMPORT_FORMAT", "IMPORT_LIMIT", "INVALID_REQUEST", "IMPORT_DELIMITER_AMBIGUOUS"].includes(
          code ?? "",
        );
      setValidation(invalid);
      if (invalid) setFocus("error");
    } finally {
      if (generation === work.current.lifetime) {
        work.current.busy = false;
        setBusy(false);
      }
    }
  };

  const restorePreparation = async () => {
    const saved = viewState().importPreparation;
    if (!saved) return false;
    if (saved.owner !== owner() || saved.sessionStamp !== api.currentSessionStamp || !canPrepare()) {
      viewState().importPreparation = null;
      return false;
    }
    const signal = work.current.lifetime.signal;
    let loaded: ContactImportResult | null = null;
    if (saved.batchId) {
      try {
        loaded = await api.getImport(saved.batchId, signal);
      } catch (exception) {
        if (signal.aborted) return false;
        if (exception instanceof ContactRequestFailure) {
          invalidate(ctl.L("DENIED"));
          ctl.importPrepared(null);
        }
        return false;
      }
    }
    if (saved.sessionStamp !== api.currentSessionStamp || !canPrepare()) {
      invalidate(ctl.L("DENIED"));
      return false;
    }
    setBatch(loaded);
    setRememberedTagChoices(saved.tagChoices ?? []);
    setText(saved.text);
    setFileName(saved.fileName);
    setSeparator(saved.separator);
    setKey(saved.key);
    setDetectedSeparator(saved.detectedSeparator);
    setFileOptionsOpen(saved.fileOptionsOpen);
    setFile(saved.file);
    setMapping({ ...saved.mapping });
    setPhase(loaded?.result ? (hasTagReview(loaded) ? 3 : 4) : saved.phase);
    setPendingOnly(loaded?.result !== null && loaded?.result !== undefined ? true : saved.pendingOnly);
    setBheliExport(saved.bheliExport);
    rememberOwner();
    work.current.visible = saved.visible;
    viewState().importBatchId = loaded?.id ?? null;
    if (saved.visible) {
      setFocus("phase");
      showDialog(taskWindow.current);
    }
    return true;
  };
  const restore = async (id: string) => {
    const signal = work.current.lifetime.signal;
    try {
      const loaded = await api.getImport(id, signal);
      rememberOwner();
      setBatch(loaded);
      setPhase(loaded.result === null ? 3 : 4);
      setPendingOnly(loaded.result !== null);
    } catch (exception) {
      if (signal.aborted) return;
      if (exception instanceof ContactRequestFailure) {
        setBatch(null);
        setPhase(1);
        ctl.importPrepared(null);
      }
    }
  };
  useImperativeHandle(ref, () => ({
    open() {
      void run(async () => {
        rememberOwner();
        work.current.visible = true;
        showDialog(taskWindow.current);
      });
    },
    invalidate,
    hasPreparation,
    preservePreparation: preserve,
    restorePreparation,
    restore,
    markClosed() {
      work.current.visible = false;
      preserve();
    },
  }));
  useEffect(() => {
    if (work.current.lifetime.signal.aborted) work.current.lifetime = new AbortController();
    const controller = work.current;
    return () => {
      preserve();
      controller.lifetime.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const separatorChar = separator === "AUTO" ? "\0" : separator === "TAB" ? "\t" : separator[0]!;
  const chooseFile = async (files: FileList | null) => {
    const signal = work.current.lifetime.signal;
    try {
      await api.ensureContext(signal);
      requireAuthority();
    } catch (exception) {
      if (!signal.aborted) setError(ctl.failure(exception));
      return;
    }
    rememberOwner();
    clearTagResolver();
    setError(null);
    setSeparatorError(false);
    setBatch(null);
    setFile(null);
    setMapping({});
    setText(null);
    setDetectedSeparator("\0");
    setKey(newKey());
    if (!files || files.length !== 1) {
      setError(ctl.L("IMPORT_FORMAT"));
      setValidation(true);
      setFocus("error");
      return;
    }
    const chosen = files[0]!;
    setFileName(chosen.name);
    const extension = chosen.name.includes(".") ? chosen.name.slice(chosen.name.lastIndexOf(".")).toLowerCase() : "";
    if (chosen.size > 1_048_576 || ![".csv", ".tsv"].includes(extension)) {
      setError(ctl.L("IMPORT_LIMIT"));
      setValidation(true);
      setFocus("error");
      return;
    }
    try {
      const bytes = await chosen.arrayBuffer();
      if (signal.aborted) return;
      setText(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
    } catch {
      if (signal.aborted) return;
      setError(ctl.L("IMPORT_FORMAT"));
      setValidation(true);
      setFocus("error");
    }
  };
  const columns = () =>
    run(async (signal) => {
      const loaded = await api.file({ text: text!, separator: separatorChar }, signal);
      setFile(loaded);
      setDetectedSeparator(loaded.detectedSeparator);
      setMapping({ ...loaded.suggestedMapping });
      setPhase(2);
      setFocus("phase");
    });
  const rememberTagChoices = () => {
    if (batch?.tagResolutions)
      setRememberedTagChoices(
        batch.tagResolutions
          .filter((item) => item.create || (item.target !== null && item.kind !== "ACTIVE"))
          .map((item) => ({
            name: item.name,
            create: item.create,
            targetId: item.create ? null : (item.target?.id ?? null),
            expectedVersion: item.create ? null : (item.target?.rowVersion ?? null),
          })),
      );
  };
  const changeMapping = (field: string, column: number) => {
    rememberTagChoices();
    const next = { ...mapping };
    if (!field) {
      for (const [name, value] of Object.entries(next)) if (value === column) delete next[name];
    } else next[field] = column;
    setMapping(next);
    setBatch(null);
    setKey(newKey());
  };
  const preview = async () => {
    const values = Object.values(mapping);
    if (!("FirstName" in mapping) || !("LastName" in mapping) || new Set(values).size !== values.length) {
      setError(ctl.L("MAPPING_ERROR"));
      const repeated = Object.entries(mapping).find(([, value]) => values.filter((item) => item === value).length > 1);
      setErrorField(!("FirstName" in mapping) ? "FirstName" : !("LastName" in mapping) ? "LastName" : repeated![0]);
      setValidation(true);
      setFocus("error");
      return;
    }
    await run(async (signal) => {
      let loaded = await api.preview(
        { text: text!, separator: separatorChar === "\0" ? detectedSeparator : separatorChar, mapping, bheliExport },
        key,
        signal,
      );
      const compatible = rememberedTagChoices.filter((choice) =>
        loaded.tagResolutions?.some((item) => item.name.toLowerCase() === choice.name.toLowerCase()),
      );
      setBatch(loaded);
      setPhase(3);
      if (compatible.length > 0) {
        loaded = await api.resolveImportTags(
          loaded.id,
          { expectedVersion: loaded.rowVersion, choices: compatible },
          signal,
        );
        setBatch(loaded);
      }
      ctl.importPrepared(loaded.id);
      if (signal.aborted) return;
      setPhase(3);
      setPendingOnly(false);
      setFocus("phase");
    });
  };
  const commit = () =>
    run(async (signal) => {
      const committed = await api.commit(batch!.id, signal, batch!.rowVersion);
      const loaded = await api.getImport(committed.id, signal, 1, true);
      setBatch(loaded);
      setPhase(4);
      setPendingOnly(true);
      setFocus("phase");
    });
  const closeTagResolution = () => {
    work.current.tagRevision++;
    setResolvingTag(null);
    setFocus("phase");
  };
  const resolveTag = (choice: ContactImportTagChoice) =>
    run(async (signal) => {
      const loaded = await api.resolveImportTags(
        batch!.id,
        { expectedVersion: batch!.rowVersion, choices: [choice] },
        signal,
      );
      setBatch(loaded);
      closeTagResolution();
      setTagOptions([]);
    });
  const loadTags = (revision: number, search: string, page: number, previous: TagDefinition[]) =>
    run(async () => {
      work.current.tagApi ??= new TagCatalogClient(ctl.tenant, ctl.user);
      const loaded = await work.current.tagApi.list({
        search,
        state: "ACTIVE",
        from: null,
        to: null,
        sort: "CREATED",
        page,
        pageSize: 25,
        selectedIds: null,
        selectedNames: null,
      });
      if (revision !== work.current.tagRevision) return;
      setTagOptions([...previous, ...loaded.items.map((row) => row.tag)]);
      setTagMore(page * 25 < loaded.total);
    });
  const searchTags = async (search: string) => {
    const revision = ++work.current.tagRevision;
    await new Promise((resolve) => setTimeout(resolve, 220));
    if (revision !== work.current.tagRevision) return;
    while (work.current.busy) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      if (revision !== work.current.tagRevision) return;
    }
    work.current.tagPage = 1;
    setTagOptions([]);
    await loadTags(revision, search, 1, []);
  };
  const openTagResolution = async (resolution: ContactImportTagResolution) => {
    setResolvingTag(resolution);
    setTagSearch("");
    setFocus("resolver");
    await searchTags("");
  };
  const moreTags = async () => {
    work.current.tagPage++;
    await loadTags(work.current.tagRevision, tagSearch, work.current.tagPage, tagOptions);
  };
  const previewPage = (page: number) =>
    run(async (signal) => {
      setBatch(await api.getImport(batch!.id, signal, page, pendingOnly));
    });
  const back = (value: number) => {
    setPhase(value);
    setError(null);
    setFocus("phase");
  };
  const close = () => {
    work.current.visible = false;
    closeDialog(taskWindow.current);
  };
  const backOrClose = () => (phase > 1 ? back(phase - 1) : close());
  const reset = () => {
    clearTagResolver();
    setText(null);
    setFileName(null);
    setFile(null);
    setBatch(null);
    setMapping({});
    setPhase(1);
    setKey(newKey());
    setError(null);
    setBheliExport(false);
    setSeparator("AUTO");
    setDetectedSeparator("\0");
    setFileOptionsOpen(false);
    ctl.importPrepared(null);
  };
  const report = () =>
    run(async (signal) => {
      downloadText(await api.importReport(batch!.id, signal), "contactos-importacion.csv");
    });
  const view = async () => {
    work.current.visible = false;
    closeDialog(taskWindow.current);
    await ctl.importComplete();
  };

  const summary =
    phase === 2 && file
      ? mappingSummary(ctl, file, mapping)
      : phase === 3 && batch && resolvingTag === null
        ? formatText(ctl.L2(importCountKey(batch.readyCount, false)), batch.readyCount)
        : null;
  const resolutionMessage = (resolution: ContactImportTagResolution) =>
    resolution.create
      ? ctl.L3("CREATE_PREPARED")
      : resolution.target
        ? formatText(ctl.L3("USE_EXISTING"), resolution.target.name)
        : resolution.kind === "NEW"
          ? ctl.L3(api.currentRights?.canManageTags === true ? "NEW_MANAGE" : "NEW_MAP")
          : ctl.L1(
              resolution.kind === "ARCHIVED"
                ? "TAG_ARCHIVED"
                : resolution.kind === "REVIEW"
                  ? "STALE"
                  : "TAG_NOT_FOUND",
            );
  const tagLineColor = (resolution: ContactImportTagResolution) =>
    resolution.target?.color ?? ctl.tagColor(resolution.name);
  const separatorOptions = [
    { value: "AUTO", label: ctl.L2("SEPARATOR_AUTO") },
    { value: ",", label: ctl.L("COMMA") },
    { value: ";", label: ctl.L("SEMICOLON") },
    { value: "TAB", label: ctl.L("TAB") },
  ];
  const tagEffects: ReactNode =
    batch?.tagResults && batch.tagResults.length > 0 ? (
      <section className={`e3-tag-results ${s}`} aria-label={ctl.L3("TAG_RESULTS")}>
        {["CREATED", "REUSED"].map((kind) => {
          const receipts = batch.tagResults!.filter((item) => item.result === kind);
          return receipts.length > 0 ? (
            <p key={kind} className={s}>
              <strong className={s}>{ctl.L3("RESULT_" + kind)}:</strong> {receipts.map((item) => item.name).join(", ")}
            </p>
          ) : null;
        })}
        {batch.pendingCount > 0 ? (
          <p className={s}>
            {ctl.L3("PENDING_REVIEW")}: {batch.pendingCount}
          </p>
        ) : null}
      </section>
    ) : null;

  return (
    <TaskWindow ref={taskWindow} className="c1-dialog c1-import" titleId="c1-import-title">
      {/* C4-01/C4-02 · C30: encabezado de una línea, el paso y el resumen una sola vez, cuerpo con scroll y barra fija. */}
      <div className={`c1-dialog-head ${s}`}>
        {phase < 4 ? (
          <button
            className={`button ghost c1-icon c1-mobile-back icon-button ${s}`}
            aria-label={ctl.L2("BACK")}
            onClick={backOrClose}
          >
            <ChatIcon name="arrow-left" />
          </button>
        ) : null}
        <h2 className={s} id="c1-import-title">
          {ctl.L("IMPORT_TITLE")}
        </h2>
        <ol className={`c1-steps ${s}`} aria-label={ctl.L("IMPORT_TITLE")}>
          {[1, 2, 3, 4].map((step) => (
            <li
              key={step}
              className={`${step < phase ? "c1-step-done" : ""} ${s}`}
              aria-current={step === phase ? "step" : undefined}
            >
              <span className={`c1-step-circle ${s}`}>{step < phase ? <ChatIcon name="check" /> : step}</span>
              <span className={s}>{ctl.L(phaseKeys[step - 1]!)}</span>
            </li>
          ))}
        </ol>
        <button
          className={`button ghost c1-icon icon-button ${s}`}
          disabled={busy}
          aria-label={ctl.L("CLOSE")}
          onClick={close}
        >
          <ChatIcon name="x" />
        </button>
      </div>
      <p className={`c2-step-of ${s}`} tabIndex={-1} ref={phaseTitle}>
        {formatText(ctl.L2("STEP_OF"), phase, 4)} · {ctl.L(phaseKeys[phase - 1]!)}
      </p>
      <p className={`c1-import-summary ${s}`} role="status">
        {summary}
      </p>
      <div className={`c1-import-body ${s}`}>
        {/* KH3 · C05: un error del usuario sale una sola vez, junto a su campo; arriba solo los de carga. */}
        {error !== null && !validation ? <SectionMessage message={error} tone="error" /> : null}
        {phase === 1 ? (
          <>
            <div className={`field c1-drop ${s}`}>
              <ChatIcon name="upload" />
              <p className={s}>{ctl.L("DROP_FILE")}</p>
              <div className={`actions c2-file-choice ${s}`}>
                <input
                  className="c2-file-input"
                  id="c1-import-file"
                  accept=".csv,.tsv,text/csv,text/tab-separated-values"
                  disabled={busy}
                  aria-label={ctl.L2("CHOOSE_FILE")}
                  aria-describedby="c1-file-help c1-import-field-error"
                  type="file"
                  onChange={(event) => void chooseFile(event.target.files)}
                />
                <label htmlFor="c1-import-file" className={`button secondary c2-file-trigger ${s}`}>
                  {ctl.L2("CHOOSE_FILE")}
                </label>
              </div>
              <span className={s} id="c1-file-help">
                {ctl.L("FILE_HELP")}
              </span>
              <FieldError
                id="c1-import-field-error"
                message={validation && phase === 1 && !separatorError ? error : null}
              />
            </div>
            <button
              className={`button secondary c1-template ${s}`}
              disabled={busy}
              onClick={() => downloadText(template, "plantilla-contactos.csv")}
            >
              <ChatIcon name="download" />
              {ctl.L("TEMPLATE_EXCEL")}
            </button>
            {fileName !== null ? <p className={s}>{fileName}</p> : null}
            <details
              className={`c2-file-options ${s}`}
              open={fileOptionsOpen || undefined}
              onToggle={(event) => setFileOptionsOpen(event.currentTarget.open)}
            >
              <summary className={s}>
                <ChatIcon name="chevron-down" />
                {ctl.L2("FILE_OPTIONS")}
              </summary>
              <div className={`field ${s}`}>
                <span className={`c1-field-label ${s}`}>{ctl.L("SEPARATOR")}</span>
                <ChoiceMenu
                  id="c2-import-separator"
                  label={ctl.L("SEPARATOR")}
                  value={separator}
                  onChange={setSeparator}
                  disabled={busy}
                  options={separatorOptions}
                  describedBy="c2-import-separator-error"
                  invalid={validation && phase === 1 && separatorError}
                  chevron={<ChatIcon name="chevron-down" />}
                  check={<ChatIcon name="check" />}
                />
                <FieldError
                  id="c2-import-separator-error"
                  message={validation && phase === 1 && separatorError ? error : null}
                />
              </div>
              {/* KH3 · C09: interruptor de Bheli (el compartido de feedback.css), no la casilla del navegador. */}
              <button
                type="button"
                className={`c1-default-switch c2-bheli-file ${s}`}
                role="switch"
                aria-checked={bheliExport ? "true" : "false"}
                disabled={busy}
                onClick={() => setBheliExport(!bheliExport)}
              >
                <span className={`c1-switch-track ${s}`} aria-hidden="true"></span>
                <span className={s}>
                  {ctl.L2("BHELI_FILE")}
                  <small className={s}>{ctl.L2("BHELI_FILE_HELP")}</small>
                </span>
              </button>
            </details>
          </>
        ) : phase === 2 && file ? (
          <ContactImportMapping
            ctl={ctl}
            file={file}
            mapping={mapping}
            onChanged={changeMapping}
            errorField={errorField}
            error={validation ? error : null}
          />
        ) : phase === 3 && batch ? (
          resolvingTag !== null ? (
            <section className={`e2-import-tags e2-import-resolver ${s}`} aria-labelledby="e3-resolver-title">
              <h4 className={s} id="e3-resolver-title" tabIndex={-1} ref={resolverTitle}>
                {resolvingTag.name}
              </h4>
              <p className={`trace ${s}`}>
                {ctl.L1("IMPORT_ROWS")}: {resolvingTag.rows.join(", ")}
              </p>
              <p className={s}>{resolutionMessage(resolvingTag)}</p>
              {resolvingTag.kind === "NEW" && api.currentRights?.canManageTags === true ? (
                <button
                  type="button"
                  className={`button secondary ${s}`}
                  disabled={busy}
                  onClick={() =>
                    void resolveTag({ name: resolvingTag.name, create: true, targetId: null, expectedVersion: null })
                  }
                >
                  {ctl.L1("CREATE_ON_IMPORT")}
                </button>
              ) : null}
              <input
                className={s}
                type="search"
                aria-label={ctl.L1("ASSIGN_SEARCH")}
                placeholder={ctl.L1("ASSIGN_SEARCH")}
                value={tagSearch}
                onChange={(event) => {
                  setTagSearch(event.target.value);
                  void searchTags(event.target.value);
                }}
              />
              <div className={`e2-import-existing ${s}`}>
                {tagOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`button ghost ${s}`}
                    disabled={busy}
                    onClick={() =>
                      void resolveTag({
                        name: resolvingTag.name,
                        create: false,
                        targetId: option.id,
                        expectedVersion: option.rowVersion,
                      })
                    }
                  >
                    {option.name}
                  </button>
                ))}
              </div>
              {tagMore ? (
                <button type="button" className={`button ghost ${s}`} disabled={busy} onClick={() => void moreTags()}>
                  {ctl.L1("MORE")}
                </button>
              ) : null}
              <button type="button" className={`button secondary ${s}`} disabled={busy} onClick={closeTagResolution}>
                {ctl.L3("BACK_PREVIEW")}
              </button>
            </section>
          ) : (
            <>
              {batch.tagResolutions && batch.tagResolutions.length > 0 ? (
                <p className={`c2-tagline ${s}`}>
                  <span className={s}>{ctl.L("TAGS")}:</span>
                  {batch.tagResolutions.map((resolution) => {
                    const actionable = resolution.kind !== "ACTIVE" || resolution.create;
                    const rows = (
                      <>
                        <ContactTagBadge name={resolution.name} color={tagLineColor(resolution)} />{" "}
                        <span className={s}>
                          {formatText(
                            ctl.L2(resolution.rows.length === 1 ? "TAG_ROWS_ONE" : "TAG_ROWS"),
                            resolution.rows.length,
                          )}
                        </span>
                      </>
                    );
                    return actionable ? (
                      <button
                        key={resolution.name}
                        type="button"
                        className={`c2-tagline-tag ${s}`}
                        disabled={busy}
                        onClick={() => void openTagResolution(resolution)}
                        title={resolutionMessage(resolution)}
                      >
                        {rows}
                      </button>
                    ) : (
                      <span
                        key={resolution.name}
                        className={`c2-tagline-tag ${s}`}
                        title={resolutionMessage(resolution)}
                      >
                        {rows}
                      </span>
                    );
                  })}
                </p>
              ) : null}
              {tagEffects}
              <ContactImportPreviewTable
                key={batch.tagResolutions && batch.tagResolutions.length > 0 ? batch.rowVersion : 0}
                ctl={ctl}
                value={batch}
                mapping={mapping}
                fileHeaders={file?.headers ?? null}
              />
              {hasImportPages(batch.visibleTotal) ? (
                <div className={`actions c2-preview-pager ${s}`}>
                  <button
                    className={`button ghost c1-icon icon-button ${s}`}
                    aria-label={ctl.L("PREVIOUS")}
                    disabled={batch.page === 1 || busy}
                    onClick={() => void previewPage(batch.page - 1)}
                  >
                    <ChatIcon name="chevron-left" />
                  </button>
                  <span className={s}>
                    {formatText(
                      ctl.L2("SHORT_PAGE"),
                      batch.page,
                      Math.max(1, Math.floor((batch.visibleTotal + 2) / 3)),
                    )}
                  </span>
                  <button
                    className={`button ghost c1-icon icon-button ${s}`}
                    aria-label={ctl.L("NEXT")}
                    disabled={batch.page * 3 >= batch.visibleTotal || busy}
                    onClick={() => void previewPage(batch.page + 1)}
                  >
                    <ChatIcon name="chevron-right" />
                  </button>
                </div>
              ) : null}
            </>
          )
        ) : phase === 4 && batch ? (
          <>
            {/* C6-03 · C31 1.9: el resultado va arriba y grande, con su ícono y una línea debajo si hay avisos. */}
            <div className={`c6-result ${s}`}>
              <span
                className={`c6-result-icon ${batch.pendingCount > 0 ? "c6-result-warn" : "c6-result-ok"} ${s}`}
                aria-hidden="true"
              >
                {batch.pendingCount > 0 ? <ChatIcon name="exclamation" /> : <ChatIcon name="check" />}
              </span>
              <div className={`c6-result-text ${s}`} role="status">
                <p className={`c6-result-title ${s}`}>
                  {formatText(ctl.L2(importCountKey(batch.savedCount, true)), batch.savedCount)}
                </p>
                {batch.pendingCount > 0 ? (
                  <p className={`c6-result-note ${s}`}>
                    {formatText(
                      ctl.L2(batch.pendingCount === 1 ? "RESULT_PENDING_ONE" : "RESULT_PENDING"),
                      batch.pendingCount,
                    )}
                  </p>
                ) : null}
                <p className={`c6-result-links ${s}`}>
                  <button type="button" className={`c6-link ${s}`} onClick={reset}>
                    {ctl.L("UPLOAD_ANOTHER")}
                  </button>
                  {hasTagReview(batch) ? (
                    <button type="button" className={`c6-link ${s}`} onClick={() => back(3)}>
                      {ctl.L1("IMPORT_TAGS")}
                    </button>
                  ) : null}
                  {batch.result?.some((row) => row.state === "RETRY") ? (
                    <button type="button" className={`c6-link ${s}`} disabled={busy} onClick={() => void commit()}>
                      {ctl.L("RETRY")}
                    </button>
                  ) : null}
                </p>
              </div>
            </div>
            {tagEffects}
            {batch.pendingCount > 0 ? (
              <details className={`c1-disclosure c6-pending ${s}`} open>
                <summary className={s}>
                  <ChatIcon name="chevron-down" />
                  {ctl.L("PENDING")} ({batch.pendingCount})
                </summary>
                <ContactImportPreviewTable
                  ctl={ctl}
                  value={batch}
                  pending
                  mapping={mapping}
                  fileHeaders={file?.headers ?? null}
                />
                {hasImportPages(batch.visibleTotal) ? (
                  <div className={`actions ${s}`}>
                    <button
                      className={`button ghost ${s}`}
                      disabled={batch.page === 1 || busy}
                      onClick={() => void previewPage(batch.page - 1)}
                    >
                      {ctl.L("PREVIOUS")}
                    </button>
                    <span className={s}>
                      {formatText(ctl.L("PAGE"), batch.page, Math.floor((batch.visibleTotal + 2) / 3))}
                    </span>
                    <button
                      className={`button ghost ${s}`}
                      disabled={batch.page * 3 >= batch.visibleTotal || busy}
                      onClick={() => void previewPage(batch.page + 1)}
                    >
                      {ctl.L("NEXT")}
                    </button>
                  </div>
                ) : null}
              </details>
            ) : null}
          </>
        ) : null}
      </div>
      {/* Barra fija: en escritorio, los botones a la derecha; en el celular, de borde a borde. */}
      {phase === 1 ? (
        <div className={`actions c1-import-foot ${s}`}>
          <button className={`button secondary ${s}`} disabled={busy} onClick={close}>
            {ctl.L("CANCEL")}
          </button>
          <button
            className={`button ${s}`}
            disabled={text === null || busy}
            aria-busy={busy ? "true" : "false"}
            onClick={() => void columns()}
          >
            {ctl.L("NEXT")}
          </button>
        </div>
      ) : phase === 2 && file ? (
        <div className={`actions c1-import-foot ${s}`}>
          <button className={`button secondary ${s}`} onClick={() => back(1)}>
            {ctl.L2("BACK")}
          </button>
          <button
            className={`button ${s}`}
            disabled={busy}
            aria-busy={busy ? "true" : "false"}
            onClick={() => void preview()}
          >
            {ctl.L("PREVIEW")}
          </button>
        </div>
      ) : phase === 3 && batch && resolvingTag === null ? (
        <div className={`actions c1-import-foot ${s}`}>
          {file ? (
            <button className={`button secondary ${s}`} disabled={busy} onClick={() => back(2)}>
              {ctl.L2("BACK")}
            </button>
          ) : null}
          <button
            className={`button ${s}`}
            disabled={busy}
            aria-busy={busy ? "true" : "false"}
            onClick={() => void commit()}
          >
            {batch.readyCount === 0 ? ctl.L("RESULT") : ctl.L("CONFIRM_IMPORT")}
          </button>
        </div>
      ) : phase === 4 && batch ? (
        <div className={`actions c1-import-foot ${s}`}>
          <button type="button" className={`button secondary ${s}`} onClick={() => void report()}>
            {ctl.L("REPORT")}
          </button>
          <button type="button" className={`button ${s}`} onClick={() => void view()}>
            {ctl.L("VIEW_CONTACTS")}
          </button>
        </div>
      ) : null}
    </TaskWindow>
  );
});

/** Columnas del archivo y el campo de Bheli de cada una (ContactImportMapping.razor). */
function ContactImportMapping({
  ctl,
  file,
  mapping,
  onChanged,
  error,
  errorField,
}: {
  ctl: ContactWorkspaceController;
  file: ContactImportFileResult;
  mapping: Record<string, number>;
  onChanged: (field: string, column: number) => void;
  error: string | null;
  errorField: string;
}) {
  const unplaced = error !== null && !(errorField in mapping);
  const fieldOptions = [
    { value: "", label: ctl.L("OMIT") },
    ...writableFields.map((name) => ({ value: name, label: ctl.field(name) })),
  ];
  const examplesFor = (column: number) =>
    file.columnExamples && column < file.columnExamples.length && file.columnExamples[column]!.length > 0
      ? file.columnExamples[column]!
      : file.examples.slice(column, column + 1);
  return (
    <>
      <div className="c2-map-head" aria-hidden="true">
        <span>{ctl.L2("IN_FILE")}</span>
        <span>{ctl.L("EXAMPLE")}</span>
        <span>{ctl.L2("FIELD_IN_BHELI")}</span>
      </div>
      {/* KH3 · C05: el error de un campo sin columna va una vez, sobre la lista; el de una columna, en su fila. */}
      {unplaced ? <FieldError id="c1-map-error" message={error} /> : null}
      <ul
        className="c2-map-list"
        id="c1-mapping-list"
        tabIndex={-1}
        aria-label={mappingSummary(ctl, file, mapping)}
        aria-describedby={unplaced ? "c1-map-error" : undefined}
      >
        {file.headers.map((header, index) => {
          const mapped = Object.entries(mapping).find(([, value]) => value === index)?.[0] ?? null;
          return [
            <li key={"c" + index} className="c2-map-row">
              <span className="c2-map-file">{header}</span>
              <span className="c2-map-example">
                {examplesFor(index)
                  .filter((value) => value.trim())
                  .join(" · ")}
              </span>
              <ChoiceMenu
                id={"c1-map-col-" + index}
                label={header + ": " + ctl.L("SOURCE")}
                describedBy="c1-map-error"
                invalid={error !== null && errorField === mapped}
                value={mapped ?? ""}
                onChange={(value) => onChanged(value, index)}
                options={fieldOptions}
                searchLabel={ctl.L2("SEARCH_FIELDS")}
                clearSearchLabel={ctl.L("CLEAR_SEARCH")}
                searchIcon={<ChatIcon name="search" />}
                clearIcon={<ChatIcon name="x" />}
                chevron={<ChatIcon name="chevron-down" />}
                check={<ChatIcon name="check" />}
              />
              {mapped !== null ? (
                <span aria-hidden="true">
                  <ChatIcon name="check" />
                </span>
              ) : (
                <span className="c2-map-omit" aria-hidden="true">
                  –
                </span>
              )}
            </li>,
            error !== null && errorField === mapped ? (
              <li key={"e" + index} className="c2-map-error">
                <FieldError id="c1-map-error" message={error} />
              </li>
            ) : null,
          ];
        })}
      </ul>
    </>
  );
}

/** Vista previa y Pendientes (ContactImportPreviewTable.razor). */
function ContactImportPreviewTable({
  ctl,
  value,
  mapping,
  fileHeaders,
  pending = false,
}: {
  ctl: ContactWorkspaceController;
  value: ContactImportResult;
  mapping: Record<string, number>;
  fileHeaders: string[] | null;
  pending?: boolean;
}) {
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(false);
  const fields = writableFields;
  const rows = value.result ?? value.rows;
  const isWarning = (row: ContactImportItem, field: string) =>
    row.reason !== null &&
    (row.reason === field ||
      (field === "Tags" && (row.reason === "TAG_NOT_FOUND" || row.reason === "TAG_ARCHIVED")) ||
      ((field === "FirstName" || field === "LastName") &&
        (row.reason === "EXISTING_CONTACT" || row.reason === "DUPLICATE_FILE")));
  const reason = (row: ContactImportItem) =>
    row.reason === "STALE_VERSION"
      ? ctl.L1("STALE")
      : row.reason === "TAG_NOT_FOUND" &&
          value.tagResolutions?.some((item) => item.rows.includes(row.number) && item.kind === "REVIEW")
        ? ctl.L1("STALE")
        : row.reason === "TAG_NOT_FOUND" &&
            value.tagResolutions?.some((item) => item.rows.includes(row.number) && item.kind === "NEW")
          ? ctl.L3("TAGS_PENDING")
          : row.reason === "TAG_ARCHIVED" || row.reason === "TAG_NOT_FOUND"
            ? ctl.L1(row.reason)
            : writableFields.includes(row.reason ?? "")
              ? ctl.field(row.reason!) +
                ": " +
                ctl.L(
                  (
                    {
                      FirstName: "NAME_ERROR",
                      LastName: "NAME_ERROR",
                      Phone: "PHONE_ERROR",
                      Email: "EMAIL_ERROR",
                      Tags: "TAGS_ERROR",
                    } as Record<string, string>
                  )[row.reason!] ?? "INVALID",
                )
              : ctl.L(
                  row.reason === "EXISTING_CONTACT" || row.reason === "DUPLICATE_FILE" ? row.reason : "PROCESS_ERROR",
                );
  const fieldValue = (row: ContactImportItem, field: string) => {
    switch (field) {
      case "FirstName":
        return row.firstName;
      case "LastName":
        return row.lastName;
      case "Email":
        return row.details?.email ?? "—";
      case "Phone":
        return phone(row.details?.phone);
      case "RelationshipStatus":
        return row.details?.relationshipStatus ? ctl.L(row.details.relationshipStatus) : ctl.L("NONE");
      case "Tags":
        return (row.details?.tags ?? []).join("; ");
      default:
        return "—";
    }
  };
  const state = (row: ContactImportItem) =>
    ctl.L(
      row.state === "READY"
        ? "READY"
        : row.state === "EXISTS" || row.state === "EXISTS_FILE"
          ? "EXISTS"
          : row.state === "SAVED"
            ? "SAVE"
            : row.state === "RETRY"
              ? "RETRY"
              : "INVALID_ROW",
    );
  if (pending) {
    const pendingRows = rows.filter((row) => row.state !== "SAVED");
    const pendingFields = fields.filter(
      (name) => name === "FirstName" || name === "LastName" || pendingRows.some((row) => isWarning(row, name)),
    );
    const shortReason = (row: ContactImportItem) =>
      writableFields.includes(row.reason ?? "") ? ctl.L2("REASON_" + row.reason!.toUpperCase()) : reason(row);
    const reasonLine = (row: ContactImportItem) =>
      writableFields.includes(row.reason ?? "")
        ? `${shortReason(row)}: ${fieldValue(row, row.reason!)}`
        : shortReason(row);
    const rowTitle = (row: ContactImportItem) => {
      const name = `${row.firstName} ${row.lastName}`.trim();
      return name.length === 0 ? `${ctl.L("ROW")} ${row.number}` : `${ctl.L("ROW")} ${row.number} · ${name}`;
    };
    return (
      <>
        {/* C6-04 · C31 1.9 (M20): en el Resultado solo las filas que fallaron, con su motivo. */}
        <div className="c6-pending-scroll" tabIndex={0} role="region" aria-label={ctl.L("PENDING")}>
          <table className="c1-table c6-pending-table">
            <thead>
              <tr>
                <th className="c6-pending-rownum">{ctl.L("ROW")}</th>
                {pendingFields.map((field) => (
                  <th key={field}>{ctl.field(field)}</th>
                ))}
                <th>{ctl.L2("REASON")}</th>
              </tr>
            </thead>
            <tbody>
              {pendingRows.map((row) => (
                <tr key={row.number}>
                  <td className="c6-pending-rownum">{row.number}</td>
                  {pendingFields.map((field) => (
                    <td key={field} className={isWarning(row, field) ? "c2-preview-warn" : undefined}>
                      {fieldValue(row, field)}
                    </td>
                  ))}
                  <td className="c6-pending-reason" title={reason(row)}>
                    {shortReason(row)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="c6-pending-mobile" aria-label={ctl.L("PENDING")}>
          {pendingRows.map((row) => (
            <li key={row.number}>
              <strong>{rowTitle(row)}</strong>
              <span title={reason(row)}>{reasonLine(row)}</span>
            </li>
          ))}
        </ul>
      </>
    );
  }
  const assignedCount = Object.keys(mapping).length;
  const skippedCount = (fileHeaders?.length ?? fields.length) - new Set(Object.values(mapping)).size;
  const warningCount = rows.filter((row) => row.reason !== null).length;
  const mapped = Object.keys(mapping).length > 0;
  const sourceName = (field: string) => {
    const column = mapping[field];
    if (column !== undefined && fileHeaders && column >= 0 && column < fileHeaders.length)
      return sourceSubtitle(fileHeaders[column]!, ctl.field(field));
    return mapped ? ctl.L2("OMITTED") : null;
  };
  const visibleFields =
    filter === "assigned"
      ? fields.filter((name) => name in mapping)
      : filter === "skipped"
        ? fields.filter((name) => !(name in mapping))
        : fields;
  const visibleRows = filter === "warning" ? rows.filter((row) => row.reason !== null) : rows;
  const pills: [string, number, string][] = [
    ["all", 0, ctl.L2("ALL_FILTER")],
    ["assigned", assignedCount, ctl.L2(assignedCount === 1 ? "ASSIGNED_ONE" : "ASSIGNED")],
    ["skipped", skippedCount, ctl.L2(skippedCount === 1 ? "SKIPPED_ONE" : "SKIPPED")],
    ["warning", warningCount, ctl.L2(warningCount === 1 ? "WARNING_ONE" : "WARNING")],
  ];
  return (
    <>
      <div className="c2-preview-pills" role="group" aria-label={ctl.L("PREVIEW")}>
        {/* C5-05: «asignadas» y «se omiten» hablan del mapeo; sin mapeo no se muestran. */}
        {pills
          .filter(([key]) => mapped || key === "all" || key === "warning")
          .map(([key, count, label]) => (
            <button
              key={key}
              type="button"
              className={`chip ${filter === key ? "active" : ""}`}
              aria-pressed={filter === key ? "true" : "false"}
              onClick={() => setFilter(key)}
            >
              {key === "all" ? label : formatText(label, count)}
            </button>
          ))}
      </div>
      <div className="c2-preview-scroll" tabIndex={0} role="region" aria-label={ctl.L("PREVIEW")}>
        <table className="c1-table c1-preview-table c2-preview-table">
          <thead>
            <tr>
              <th className="c2-preview-rownum">{ctl.L("ROW")}</th>
              {visibleFields.map((field) => {
                const omitted = mapped && !(field in mapping);
                const source = sourceName(field);
                return (
                  <th key={field} className={omitted ? "c2-preview-omitted" : ""}>
                    <span className="c2-preview-field">{ctl.field(field)}</span>
                    {source !== null ? <span className="c2-preview-source">{source}</span> : null}
                  </th>
                );
              })}
              <th>{ctl.L2("STATE")}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.number}>
                <td className="c2-preview-rownum">{row.number}</td>
                {visibleFields.map((field) => {
                  const omitted = mapped && !(field in mapping);
                  const warn = !omitted && isWarning(row, field);
                  return (
                    <td
                      key={field}
                      className={`${omitted ? "c2-preview-omitted" : ""} ${warn ? "c2-preview-warn" : ""}`}
                    >
                      {omitted ? (
                        <span>{ctl.L2("OMITTED")}</span>
                      ) : (
                        <>
                          {fieldValue(row, field)}
                          {warn ? (
                            <span className="c2-preview-flag" title={reason(row)}>
                              <ChatIcon name="exclamation" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </td>
                  );
                })}
                <td>
                  <span>{state(row)}</span>
                  {row.reason !== null ? (
                    <details className="e3-row-diagnostic c1-disclosure">
                      <summary>
                        <ChatIcon name="chevron-down" />
                        {ctl.L2("VIEW_DETAIL")}
                      </summary>
                      <p>{reason(row)}</p>
                    </details>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="c2-preview-mobile">
        {visibleFields.map((field) => {
          const omitted = mapped && !(field in mapping);
          const warnings = omitted ? 0 : visibleRows.filter((row) => isWarning(row, field)).length;
          const source = sourceName(field);
          const label = formatText(ctl.L2(warnings === 1 ? "WARNING_ONE" : "WARNING"), warnings);
          return (
            <details key={field} className="c1-disclosure c2-preview-mrow">
              <summary>
                <span>{ctl.field(field)}</span>
                {source !== null ? <small>{source}</small> : null}
                <ChatIcon name="chevron-down" />
                {warnings > 0 ? (
                  <span className="c2-preview-flag" role="img" aria-label={label} title={label}>
                    <ChatIcon name="exclamation" />
                  </span>
                ) : null}
              </summary>
              <div>
                {(expanded ? visibleRows : visibleRows.slice(0, 3)).map((row) => {
                  const warn = !omitted && isWarning(row, field);
                  return (
                    <p key={row.number} className={warn ? "c2-preview-warn" : ""}>
                      <strong>{row.number}.</strong> {omitted ? ctl.L2("OMITTED") : fieldValue(row, field)}
                      {warn ? (
                        <span className="c2-preview-flag" title={reason(row)}>
                          <ChatIcon name="exclamation" />
                        </span>
                      ) : null}
                    </p>
                  );
                })}
                {!expanded && visibleRows.length > 3 ? (
                  <button type="button" className="button secondary" onClick={() => setExpanded(true)}>
                    {formatText(ctl.L2("VIEW_ALL"), visibleRows.length)}
                  </button>
                ) : null}
              </div>
            </details>
          );
        })}
      </div>
    </>
  );
}
